import { JwtSessionData } from './jwt-session-data.js';
import { JwtSession } from './jwt-session.js';
import { nowInSeconds } from '@iwentsourcing/core/utils/now-in-seconds.js';
import { CustomError } from '@iwentsourcing/core/error/custom-error.js';
import { RpcClient } from '../api/create-rpc-client.js';
import { reusableResolvable } from '@iwentsourcing/core/utils/reusable-resolvable.js';
import { JwtSessionStorage } from './jwt-session-storage.js';
import { EventAuth } from '@iwentsourcing/core/event-store/event-auth.js';
import { parseJwt } from '@iwentsourcing/core/authentication/jwt/parse-jwt.js';
import { JwtPayload } from '@iwentsourcing/core/authentication/jwt/jwt-payload.js';
import type { Lock } from '@iwentsourcing/core/lock/lock.js';

export function createJwtSessionHandler(
  rpcClient: RpcClient,
  opts: {
    data: JwtSessionData;
    signal: AbortSignal;
    sessionStorage: JwtSessionStorage<JwtSessionData>;
    lock: Lock;
  },
): JwtSession {
  let token = opts.data;

  const reusableRefresh = reusableResolvable<string>();

  async function handleSessionResponse(
    data:
      | undefined
      | {
          id_token: string;
          refresh_token: string;
          expires_in: number;
        },
  ): Promise<string> {
    if (!data) {
      throw new CustomError('invalid session', {});
    }

    token = {
      accessToken: data.id_token,
      refreshToken: data.refresh_token,
      exp: nowInSeconds() + data.expires_in,
      provider: token.provider,
      realm: token.realm,
      tenant: token.tenant,
      username: token.username,
    };
    await opts.sessionStorage.save(token);

    return token.accessToken;
  }

  return {
    async resolveAuth(): Promise<EventAuth> {
      // TODO improve, session data could extract it ahead
      const token = await this.resolveToken();
      const jwt = parseJwt(token, 1, JwtPayload);
      return {
        aud: jwt.aud,
        sub: jwt.sub,
        iss: jwt.iss,
      };
    },
    async resolveToken(): Promise<string> {
      if (opts.signal.aborted) {
        throw new CustomError('aborted', {});
      }

      const now = nowInSeconds();

      if (token && token.exp > now + 20) {
        return token.accessToken;
      }

      // Shared per realm (not tenant): one JWT authenticates the user across
      // every tenant of the realm, and refresh tokens rotate, so sibling
      // tenant handlers must serialize on one lock to refresh exactly once.
      const lockName = `iwentsourcing:auth-refresh:${token.realm}:${token.provider}:${encodeURIComponent(token.username)}`;

      return reusableRefresh.resolve(() =>
        opts.lock.acquire(
          lockName,
          async () => {
            const nowAfterWait = nowInSeconds();
            const latest = await opts.sessionStorage.load();
            if (latest) {
              if (latest.exp > nowAfterWait + 20) {
                token = latest;
                return token.accessToken;
              }
              token = latest;
            }

            const response = await rpcClient['auth.refresh'].call(
              {
                tenant: token.tenant,
                realm: token.realm,
                provider: token.provider,
                refreshToken: token.refreshToken,
                username: token.username,
              },
              opts.signal,
            );

            return handleSessionResponse(response);
          },
          opts.signal,
        ),
      );
    },
  };
}
